import React, { memo, FC, useRef, useEffect, useCallback, MutableRefObject, useState } from 'react';
import ProductDocumentField, { ProductDocumentFieldFileInfo, ProductDocumentFieldRef } from './ProductDocumentField';
import FormContainer, { FormContainerChild, FormContainerSubmitHandler } from '../../../../../legacy-components/form/container/FormContainer';
import { ProductPublishFormData, ProductDocumentPublishedData } from '../../models/products.model';
import ActionInliner from '../../../../../legacy-components/form/action/ActionInliner';
import { IconButton, Tooltip, Snackbar } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { FormattedMessage } from 'react-intl';
import ProductDocumentFormConfirmDialog, { ProductDocumentFormConfirmDialogRef } from './ProductDocumentFormConfirmDialog';
import SpecDocumentationService from '../../../../../libs/resources/documentation/DocumentationService';
import { FORM_ERROR } from 'final-form';
import ClearIcon from '@material-ui/icons/Clear';
import ProductDocumentPublishAction from './ProductDocumentPublishAction';
import { sanitizeText } from '../FileXSSRemover';
import Alert from '@material-ui/lab/Alert';
import { resolveAPIErrorMessage } from '../../../../../modus/components/error-message/ErrorMessage';

interface ProductDocumentFormProps {
    docInfo: ProductDocumentFieldFileInfo,
    onDelete: () => void;
    onChange: (data: ProductDocumentPublishedData) => void;
    onCreate: (data: ProductDocumentFieldFileInfo) => void;
    productName: string;
}

interface ProductDocumentSyncFormProps {
    onCreate: (data: ProductDocumentFieldFileInfo) => void;
    docInfo?: ProductDocumentFieldFileInfo;
    docFieldRef: MutableRefObject<ProductDocumentFieldRef | undefined>;
    productName: string;
    onError?: (error: string | null) => void;
}

const DocumentFieldControls: FC<{
    onDelete: () => void,
    onClear: () => void,
    onChange: (data: ProductDocumentPublishedData) => void,
    docInfo: ProductDocumentFieldFileInfo,
    productName: string;
}> = ({ onDelete, docInfo, onClear, productName, onChange }) => {
    return (
        docInfo.loaded ? (
            <ActionInliner>
                <ProductDocumentPublishAction docInfo={docInfo} productName={productName} onChange={onChange} />
                <Tooltip enterDelay={1000} title={<FormattedMessage defaultMessage="Delete Document" />}>
                    <IconButton onClick={onDelete}  disabled={true} size="small">
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </ActionInliner>
        ) : (
                <ActionInliner>
                    <Tooltip enterDelay={1000} title={<FormattedMessage defaultMessage="Clear" />}>
                        <IconButton onClick={onClear} size="small">
                            <ClearIcon />
                        </IconButton>
                    </Tooltip>
                </ActionInliner>
            )
    );
}

const ProductDocumentSyncForm: FC<ProductDocumentSyncFormProps> = ({ docInfo, docFieldRef, productName, onCreate, onError }) => {
  const [sanitizedFile, setSanitizedFile] = useState<File>();
  // Create a ref for the FileReader to ensure we have a new instance for each component
  const fileReaderRef = useRef<FileReader | null>(null);

  useEffect(() => {
    // Clear any previous error messages when a new file is selected
    if (onError) {
      onError(null);
    }
    if (docInfo?.file && !docInfo.loaded && docFieldRef?.current) {
      docFieldRef.current.reset();
    }
    
    if(!docInfo?.file) {
        return;
    }
    // Abort any existing FileReader operation first
    if (fileReaderRef.current && fileReaderRef.current.readyState === FileReader.LOADING) {
      fileReaderRef.current.abort();
    }
    // Create a new FileReader instance for each file
    fileReaderRef.current = new FileReader();
    
    fileReaderRef.current.onload = (e: ProgressEvent<FileReader>) => {
      const sanitizedContent = sanitizeText(e?.target?.result as string) as string;
      // Create a new file with the sanitized content using the original file type and name
      const blob = new Blob([sanitizedContent], { type: docInfo?.file?.type });
      const newFile = new File([blob], docInfo?.file?.name as string, { type: docInfo?.file?.type });
      setSanitizedFile(newFile);
    };
    fileReaderRef.current.onerror = () => {
      if (onError) {
        onError("Error reading file. Please try again with a different file.");
      }
    };
    fileReaderRef.current.readAsText(docInfo.file);
    // Cleanup function to abort any ongoing read operations when unmounting
    return () => {
      if (fileReaderRef.current && fileReaderRef.current.readyState === FileReader.LOADING) {
        fileReaderRef.current.abort();
      }
    };
  }, [docInfo?.file, docFieldRef, onError]);

  const triggerUpload = useCallback(async (resolve: (value: unknown) => void) => {
    if (docInfo?.file && sanitizedFile) {
        try {
            if (onError) {
                onError(null);
            }
            docFieldRef?.current?.progress(0);
            const response = await SpecDocumentationService.uploadDocument({
                file: sanitizedFile as File,
                productName,
                name: productName
            });

            docFieldRef?.current?.progress(100);
            
            // Clear any error messages on successful upload
            if (onError) {
                onError(null);
            }

            onCreate({
                id: response.id,
                fileInfo: docInfo.fileInfo,
                openApiSpecId: response.id,
                loaded: true,
                isNew: docInfo.isNew
            });

            resolve(null);
        } catch (error) {
            docFieldRef.current?.error();
            
            let message = resolveAPIErrorMessage(error, "Unknown error occurred while uploading...");
            // Notify parent component about the error for the Snackbar
            if (onError) {
                onError(message);
            }
            
            resolve({
                [FORM_ERROR]: {
                    documentation: message
                }
            });
        }
    } else {
        setTimeout(() => {
            triggerUpload(resolve);
        }, 1000);
    }
},[docFieldRef, docInfo, onCreate, productName, sanitizedFile, onError]);
  useEffect(() => {
    if(!sanitizedFile || !triggerUpload) {
        return;
    }
    triggerUpload(async () => {});
  },[sanitizedFile, triggerUpload]);


    
    const onSubmit: FormContainerSubmitHandler<ProductPublishFormData> = () => () => {
        return triggerUpload(async () => {});
    }

    const FormComponents: FormContainerChild<ProductPublishFormData> = (_formRenderProps, _containerOptions, triggerSubmit) => {
        const onSubmit = useRef(() => triggerSubmit());

        useEffect(() => {
            if (!docInfo?.loaded) {
                onSubmit.current();
            }
        }, [onSubmit]);

        return (<></>);
    }

    return <FormContainer previewErrors={true} FormProps={{ onSubmit }} children={FormComponents} />;
}

function ProductDocumentForm({ docInfo, onDelete, productName, onCreate, onChange }: ProductDocumentFormProps) {
    const confirmDialogRef = useRef<ProductDocumentFormConfirmDialogRef>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // Create a unique file ID to help track when file changes
    const fileId = docInfo?.id || (docInfo?.file ? `${docInfo.file.name}-${docInfo.file.lastModified}` : 'new-file');
    
    const onDeleteHandler = useCallback(() => confirmDialogRef?.current?.open(), []);
    // Create a new ref instance when fileId changes to force state reset
    const docFieldRef = useRef<ProductDocumentFieldRef>();
    const handleCloseSnackbar = useCallback(() => {
        setErrorMessage(null);
    }, []);
    
    // Callback to handle error message display
    const handleError = useCallback((error: string | null) => {
        setErrorMessage(error);
    }, []);
    
    // Reset upload status when docInfo changes (new file selected/deleted)
    useEffect(() => {
        setErrorMessage(null);
        // Reset the document field's state when file changes
        if (docFieldRef?.current) {
            docFieldRef.current.reset();
        }
    }, [fileId]); 

    return (
        <>
            <ProductDocumentFormConfirmDialog productName={productName} ref={confirmDialogRef} docInfo={docInfo} onSuccess={onDelete} />
            
            <Snackbar 
                open={!!errorMessage} 
                autoHideDuration={5000} 
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="error">
                    {errorMessage}
                </Alert>
            </Snackbar>
            
            <ProductDocumentSyncForm 
                key={`sync-${fileId}`} // Add key to force remount when file changes
                onCreate={onCreate} 
                productName={productName} 
                docFieldRef={docFieldRef} 
                docInfo={docInfo} 
                onError={handleError}
            />

            {docInfo && (
                <ProductDocumentField
                    key={fileId} // Using fileId as key to force component remount when file changes
                    showPublishStatus={true}
                    ref={docFieldRef}
                    docInfo={docInfo}
                    controls={
                        <DocumentFieldControls
                            productName={productName}
                            onChange={onChange}
                            onDelete={onDeleteHandler}
                            onClear={onDelete}
                            docInfo={docInfo}
                        />
                    }
                />
            )}
        </>
    )
}

ProductDocumentForm.propTypes = {

}

export default memo(ProductDocumentForm);

